import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import {
	SolunoActivityCode,
	SolunoActivityCodeTypes,
	SolunoGLAccount,
} from '@models/soluno';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { Observable } from 'rxjs';
import { StatementOfAccountsDispatch } from '@state/matter/statementOfAccounts';
import { getSolunoActivityCodeLabelDescription } from '@core/util/accounting/soluno';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchableDropdownItems } from 'src/app/shared/atomic/atoms/searchable-dropdown/searchable-dropdown.types';
import { MatterStatementOfAccount } from '@models/statementOfAccount';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import {
	NotApplicableValue,
	SolunoAccountingItem,
	SolunoItem,
} from '../../accounting.models';

@UntilDestroy()
@Component({
	selector: 'app-soluno-accounting-items-table',
	templateUrl: './soluno-accounting-items-table.component.html',
	styleUrls: ['./soluno-accounting-items-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SolunoAccountingItemsTableComponent implements OnInit {
	public displayedColumns: string[] = ['title', 'amount', 'activityCode'];

	public items: SolunoItem[] = [];

	@Select(BusinessState.getSolunoActivityCodes) activityCodes$: Observable<
		SolunoActivityCode[]
	>;
	@ViewSelectSnapshot(BusinessState.getSolunoActivityCodes)
	activityCodes: SolunoActivityCode[];
	@ViewSelectSnapshot(BusinessState.getSolunoGLAccounts)
	glAccounts: SolunoGLAccount[];
	public activityCodesDropdownOptions: SearchableDropdownItems[] = [];

	@Input('items') set _items(items: SolunoItem[]) {
		this.items = items;
		this.total = items.reduce(
			(acc, item) => acc + item.accountingItem.amount,
			0,
		);
	}

	@Input() title = '';

	@Input() hst: number = null;
	@Input() hstTotal: number = null;
	@Input() activityCodeColumnTitle = 'Activity Code';
	public total = 0;

	constructor(
		private statementOfAccountDispatch: StatementOfAccountsDispatch,
		private analyticsService: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.activityCodes$
			.pipe(untilDestroyed(this))
			.subscribe((activityCodes: SolunoActivityCode[]) => {
				this.activityCodesDropdownOptions = activityCodes.map(
					(activityCode: SolunoActivityCode) => {
						return {
							label: getSolunoActivityCodeLabelDescription(activityCode),
							value: activityCode,
						};
					},
				);

				this.activityCodesDropdownOptions.unshift({
					label: 'N/A',
					value: {
						id: NotApplicableValue,
						description: NotApplicableValue,
						type: SolunoActivityCodeTypes.NA,
					} as SolunoActivityCode,
				});
			});
	}

	onActivityCodeChange(event: SolunoActivityCode, item: SolunoItem) {
		this.analyticsService.logEvent(
			AnalyticsEvent.SOLUNO_FILE_ADDED_ACTIVITY_CODE,
		);

		const result: MatterStatementOfAccount = { ...item.statement };

		if (event) {
			result.solunoActivityCode = event;

			this.statementOfAccountDispatch.SetStatement(item.statement.id, {
				...result,
			});
		}
	}

	getBackupValue(activityCode: SolunoActivityCode): string {
		return getSolunoActivityCodeLabelDescription(activityCode);
	}

	trackByFn(index: number, item: SolunoAccountingItem) {
		return item.statementId;
	}
}
